import { calendarDateSchema, type CalendarDate } from "./timing.js";
import type { ServiceProfileProjection } from "./service-profile-projection.js";
import type { DerivedServiceFit, QueueCode } from "./service.js";
import type { TimingProjectionReason } from "./timing-engine.js";

export const COMING_HORIZON_DAYS = 90;

export interface QueueRuleSet {
  readonly comingHorizonDays: number;
}

export const DEFAULT_QUEUE_RULES: QueueRuleSet = Object.freeze({
  comingHorizonDays: COMING_HORIZON_DAYS,
});

export type LabLifecycleStatus = "active" | "moved" | "closed";

export interface QueueContactContext {
  readonly hasUsableCalibrationContact: boolean;
}

export interface ServiceProfileQueueInput {
  readonly projection: ServiceProfileProjection;
  readonly businessDate: CalendarDate;
  readonly profileIsActive: boolean;
  readonly labLifecycleStatus: LabLifecycleStatus;
  readonly contactContext: QueueContactContext;
  readonly rules?: QueueRuleSet;
}

export type QueueReasonCode =
  | "unsupported_accreditation_requirement"
  | "relative_overdue"
  | "due_soon_window_active"
  | "likely_overdue_after_due_soon_window"
  | "attention_date_due"
  | "timing_unknown"
  | "attention_date_within_coming_horizon"
  | "attention_date_beyond_coming_horizon";

export type KnowledgeGapCode =
  | "timing_unknown"
  | "quantity_unknown"
  | "provider_unknown"
  | "calibration_contact_unknown"
  | "accreditation_requirement_unknown";

/**
 * Human-readable labels for queue reason codes. These stay beside the canonical
 * codes so a browser surface never invents its own vocabulary or drifts from the
 * server's reason set. Labels are display strings only: `queueReasonCode`
 * remains the contract value.
 */
export const queueReasonCodeLabels: Record<QueueReasonCode, string> = {
  unsupported_accreditation_requirement:
    "Accreditation is required but is not supported in V1.",
  relative_overdue: "The latest information says service is overdue.",
  due_soon_window_active: "The observed due-soon window is active.",
  likely_overdue_after_due_soon_window:
    "The observed due-soon window passed without newer timing.",
  attention_date_due: "The sales attention date is due.",
  timing_unknown: "Timing is not known yet.",
  attention_date_within_coming_horizon:
    "The sales attention window is approaching.",
  attention_date_beyond_coming_horizon:
    "The next sales attention date is beyond the planning horizon.",
};

/** Human-readable labels for the facts a Service Profile does not know yet. */
export const knowledgeGapCodeLabels: Record<KnowledgeGapCode, string> = {
  timing_unknown: "Timing unknown",
  quantity_unknown: "Quantity unknown",
  provider_unknown: "Provider unknown",
  calibration_contact_unknown: "Calibration contact unknown",
  accreditation_requirement_unknown: "Accreditation need unknown",
};

export interface QueueReadModel {
  readonly queueCode: QueueCode;
  readonly queueReasonCode: QueueReasonCode;
  readonly nextMeaningfulSalesDate: CalendarDate | null;
  readonly nextSalesDateReason: TimingProjectionReason | null;
  readonly knowledgeGaps: readonly KnowledgeGapCode[];
  readonly calculatedForBusinessDate: CalendarDate;
  readonly projectionRuleVersion: number;
  readonly serviceFit: DerivedServiceFit;
}

/**
 * Classify one existing Service Profile projection for one explicit business
 * date. Queue membership is a read-model value and this function performs no
 * persistence, timing rebuild, or system-clock access.
 */
export function classifyServiceProfileQueue(
  input: ServiceProfileQueueInput,
): QueueReadModel | null {
  const businessDate = calendarDateSchema.parse(input.businessDate);
  const rules = input.rules ?? DEFAULT_QUEUE_RULES;
  validateRules(rules);

  if (!input.profileIsActive || input.labLifecycleStatus === "closed") {
    return null;
  }

  const projection = input.projection;
  const knowledgeGaps = calculateKnowledgeGaps(
    projection,
    input.contactContext,
  );
  const base = {
    nextMeaningfulSalesDate: projection.nextMeaningfulSalesDate,
    nextSalesDateReason: projection.nextSalesDateReason,
    knowledgeGaps,
    calculatedForBusinessDate: businessDate,
    projectionRuleVersion: projection.projectionRuleVersion,
    serviceFit: projection.serviceFit,
  } as const;

  if (projection.serviceFit === "disqualified") {
    return freezeQueueReadModel({
      ...base,
      queueCode: "DISQUALIFIED_SERVICE_NEED",
      queueReasonCode: "unsupported_accreditation_requirement",
    });
  }

  if (
    projection.timingPrecision === "relative" &&
    projection.timingRelativeState === "overdue"
  ) {
    return freezeQueueReadModel({
      ...base,
      queueCode: "NOW",
      queueReasonCode: "relative_overdue",
    });
  }

  if (
    projection.timingPrecision === "relative" &&
    projection.timingRelativeState === "due_soon" &&
    projection.derivedDueStartDate !== null &&
    projection.derivedDueEndDate !== null
  ) {
    const dueStart = calendarDateSchema.parse(projection.derivedDueStartDate);
    const dueEnd = calendarDateSchema.parse(projection.derivedDueEndDate);

    if (compareCalendarDates(businessDate, dueStart) >= 0) {
      if (compareCalendarDates(businessDate, dueEnd) <= 0) {
        return freezeQueueReadModel({
          ...base,
          queueCode: "NOW",
          queueReasonCode: "due_soon_window_active",
        });
      }

      return freezeQueueReadModel({
        ...base,
        queueCode: "NOW",
        queueReasonCode: "likely_overdue_after_due_soon_window",
      });
    }
  }

  if (
    projection.timingPrecision === null ||
    projection.timingPrecision === "unknown" ||
    projection.nextMeaningfulSalesDate === null
  ) {
    return freezeQueueReadModel({
      ...base,
      queueCode: "INVESTIGATE",
      queueReasonCode: "timing_unknown",
    });
  }

  const nextMeaningfulSalesDate = calendarDateSchema.parse(
    projection.nextMeaningfulSalesDate,
  );
  if (compareCalendarDates(nextMeaningfulSalesDate, businessDate) <= 0) {
    return freezeQueueReadModel({
      ...base,
      queueCode: "NOW",
      queueReasonCode: "attention_date_due",
    });
  }

  const comingHorizon = addDays(businessDate, rules.comingHorizonDays);
  if (compareCalendarDates(nextMeaningfulSalesDate, comingHorizon) <= 0) {
    return freezeQueueReadModel({
      ...base,
      queueCode: "COMING",
      queueReasonCode: "attention_date_within_coming_horizon",
    });
  }

  return freezeQueueReadModel({
    ...base,
    queueCode: "SLEEPING",
    queueReasonCode: "attention_date_beyond_coming_horizon",
  });
}

function calculateKnowledgeGaps(
  projection: ServiceProfileProjection,
  contactContext: QueueContactContext,
): readonly KnowledgeGapCode[] {
  const gaps: KnowledgeGapCode[] = [];

  if (
    projection.timingPrecision === null ||
    projection.timingPrecision === "unknown" ||
    projection.nextMeaningfulSalesDate === null
  ) {
    gaps.push("timing_unknown");
  }
  if (projection.currentQuantityValue === null) {
    gaps.push("quantity_unknown");
  }
  if (
    projection.currentProviderName === null ||
    projection.currentProviderName.trim().length === 0
  ) {
    gaps.push("provider_unknown");
  }
  if (!contactContext.hasUsableCalibrationContact) {
    gaps.push("calibration_contact_unknown");
  }
  if (projection.accreditationRequirement === "unknown") {
    gaps.push("accreditation_requirement_unknown");
  }

  return Object.freeze(gaps.sort());
}

function freezeQueueReadModel(
  result: Omit<QueueReadModel, "knowledgeGaps"> & {
    readonly knowledgeGaps: readonly KnowledgeGapCode[];
  },
): QueueReadModel {
  return Object.freeze({
    ...result,
    knowledgeGaps: Object.freeze([...result.knowledgeGaps]),
  });
}

function compareCalendarDates(left: CalendarDate, right: CalendarDate): number {
  return left < right ? -1 : left > right ? 1 : 0;
}

interface CivilDate {
  readonly year: number;
  readonly month: number;
  readonly day: number;
}

function addDays(date: CalendarDate, days: number): CalendarDate {
  const civilDate = parseCivilDate(date);
  return formatCalendarDate(civilFromDays(daysFromCivil(civilDate) + days));
}

function parseCivilDate(date: CalendarDate): CivilDate {
  const parsed = calendarDateSchema.parse(date);
  return {
    year: Number(parsed.slice(0, 4)),
    month: Number(parsed.slice(5, 7)),
    day: Number(parsed.slice(8, 10)),
  };
}

function formatCalendarDate(date: CivilDate): CalendarDate {
  return [
    String(date.year).padStart(4, "0"),
    String(date.month).padStart(2, "0"),
    String(date.day).padStart(2, "0"),
  ].join("-") as CalendarDate;
}

// Proleptic-Gregorian civil-date conversion. It never consults host timezone
// or daylight-saving rules.
function daysFromCivil(date: CivilDate): number {
  const adjustedYear = date.year - (date.month <= 2 ? 1 : 0);
  const era = Math.floor(adjustedYear / 400);
  const yearOfEra = adjustedYear - era * 400;
  const monthPrime = date.month + (date.month > 2 ? -3 : 9);
  const dayOfYear = Math.floor((153 * monthPrime + 2) / 5) + date.day - 1;
  const dayOfEra =
    yearOfEra * 365 +
    Math.floor(yearOfEra / 4) -
    Math.floor(yearOfEra / 100) +
    dayOfYear;
  return era * 146097 + dayOfEra - 719468;
}

function civilFromDays(days: number): CivilDate {
  const shiftedDays = days + 719468;
  const era = Math.floor(shiftedDays / 146097);
  const dayOfEra = shiftedDays - era * 146097;
  const yearOfEra = Math.floor(
    (dayOfEra -
      Math.floor(dayOfEra / 1460) +
      Math.floor(dayOfEra / 36524) -
      Math.floor(dayOfEra / 146096)) /
      365,
  );
  let year = yearOfEra + era * 400;
  const dayOfYear =
    dayOfEra -
    (365 * yearOfEra + Math.floor(yearOfEra / 4) - Math.floor(yearOfEra / 100));
  const monthPrime = Math.floor((5 * dayOfYear + 2) / 153);
  const day = dayOfYear - Math.floor((153 * monthPrime + 2) / 5) + 1;
  const month = monthPrime + (monthPrime < 10 ? 3 : -9);
  year += month <= 2 ? 1 : 0;

  return { year, month, day };
}

function validateRules(rules: QueueRuleSet): void {
  if (
    !Number.isInteger(rules.comingHorizonDays) ||
    rules.comingHorizonDays < 0
  ) {
    throw new Error("comingHorizonDays must be a non-negative integer");
  }
}
